import axios from 'axios';

const proxyURL = process.env.VUE_APP_PROXY_URL ? `${process.env.VUE_APP_PROXY_URL}/` : '';
let dryRun;
if (process.env.VUE_APP_FLEXI_DRY_RUN) {
  dryRun = JSON.parse(process.env.VUE_APP_FLEXI_DRY_RUN.toLowerCase());
} else if (localStorage.flexiDryRun) {
  dryRun = JSON.parse(localStorage.flexiDryRun.toLowerCase());
} else {
  dryRun = false;
}

function endpointURL({ apiURL, endpoint }) {
  return proxyURL + apiURL + endpoint;
}

function extractToken({ note }) {
  const re = /([\s\S]*)\n\nDM-TOKEN:(.*)/gm;
  const matches = re.exec(note);
  const strippedNote = matches ? matches[1] : note;
  const token = matches ? matches[2] : false;
  return {
    token,
    note: strippedNote,
  };
}

const FlexiRequest = async ({
  method,
  apiURL,
  endpoint,
  params,
  data,
  username,
  password,
  token,
}) => {
  const dryRunParam = dryRun ? { 'dry-run': true } : null;
  const searchParams = new URLSearchParams({
    ...params,
    ...dryRunParam,
  });
  const headers = {
    ...(token ? { 'x-authSessionId': token } : undefined),
    ...{ Accept: 'application/json' },
  };
  const auth = (!token && username && password) ? { username, password } : undefined;

  const response = await axios({
    method,
    url: endpointURL({ apiURL, endpoint }),
    params: searchParams,
    data,
    headers,
    auth,
  });
  return response;
};

const Flexi = {
  methods: {
    async $_Flexi_tryConnection({ apiURL }) {
      try {
        const endpoint = '/status-check';
        const params = { quick: true };
        const response = await FlexiRequest({
          method: 'get',
          apiURL,
          endpoint,
          params,
        });
        // response.data.status.appServerRunning could be used for further checking
        const status = { version: response.data.status.version };
        return status;
      } catch (error) {
        if (!error.response && error.message === 'Network Error') {
          return false;
        }
        throw error;
      }
    },
    async $_Flexi_login({ apiURL, username, password }) {
      const endpoint = '/login-logout/login';
      const data = new URLSearchParams({ username, password });
      const response = await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        data,
      });
      if (response.data.success) {
        const token = response.data.authSessionId;
        return token;
      }
      return false;
    },
    async $_Flexi_logout({ apiURL, username, token }) {
      const endpoint = `/status/user/${username}/logout`;
      await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        token,
      });
      return true;
    },
    async $_Flexi_keepAlive({ apiURL, token }) {
      const endpoint = '/login-logout/session-keep-alive';
      await FlexiRequest({
        method: 'get',
        apiURL,
        endpoint,
        token,
      });
      return true;
    },
    async $_Flexi_listCompanies({
      apiURL,
      username,
      password,
      token,
    }) {
      const endpoint = '/c';
      const response = await FlexiRequest({
        method: 'get',
        apiURL,
        endpoint,
        username,
        password,
        token,
      });
      let companies = [];
      if (Array.isArray(response.data.companies.company)) {
        companies = response.data.companies.company.map((company) => ({
          name: company.nazev,
          id: company.dbNazev,
        }));
      } else {
        companies = [{
          name: response.data.companies.company.nazev,
          id: response.data.companies.company.dbNazev,
        }];
      }
      return companies;
    },
    async $_Flexi_retrieveUserNote({
      apiURL,
      username,
      password,
      token,
      company,
    }) {
      const endpoint = `/c/${company}/uzivatel/code:${username}`;
      const response = await FlexiRequest({
        method: 'get',
        apiURL,
        endpoint,
        username,
        password,
        token,
      });
      const note = response.data.winstrom.uzivatel[0].poznam;
      return note;
    },
    async $_Flexi_updateUserNote({
      apiURL,
      username,
      password,
      token,
      company,
      note,
    }) {
      const endpoint = `/c/${company}/uzivatel/code:${username}`;
      const data = {
        winstrom: {
          uzivatel: [
            {
              poznam: `${note}`,
            },
          ],
        },
      };
      await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        data,
        username,
        password,
        token,
      });
      return true;
    },
    async $_Flexi_retrieveToken({
      apiURL,
      username,
      password,
      token,
      company,
    }) {
      const note = await this.$_Flexi_retrieveUserNote({
        apiURL,
        username,
        password,
        token,
        company,
      });
      const extractedNote = extractToken({ note });
      const documentManagerToken = extractedNote.token;
      return documentManagerToken;
    },
    async $_Flexi_addToken({
      apiURL,
      username,
      password,
      token,
      company,
      documentManagerToken,
    }) {
      const commonParams = {
        apiURL, username, password, token, company,
      };

      const note = await this.$_Flexi_retrieveUserNote(commonParams);
      const extractedNote = extractToken({ note });
      const updatedNote = `${extractedNote.note}\n\nDM-TOKEN:${documentManagerToken}`;

      await this.$_Flexi_updateUserNote({ ...commonParams, note: updatedNote });
      return true;
    },
    async $_Flexi_removeToken({
      apiURL,
      username,
      password,
      token,
      company,
    }) {
      const commonParams = {
        apiURL, username, password, token, company,
      };

      const note = await this.$_Flexi_retrieveUserNote(commonParams);
      const extractedNote = extractToken({ note });

      await this.$_Flexi_updateUserNote({ ...commonParams, note: extractedNote.note });
      return true;
    },
    async $_Flexi_checkButton({
      apiURL,
      username,
      password,
      token,
      company,
      button,
    }) {
      const commonParams = {
        apiURL, username, password, token, company,
      };

      const endpoint = `/c/${company}/custom-button`;
      const response = await FlexiRequest({
        method: 'get',
        apiURL,
        endpoint,
        username,
        password,
        token,
      });
      const filteredButtons = response.data.winstrom['custom-button'].filter((element) => (element.kod === button.code));

      if (filteredButtons.length !== 1) {
        return false;
      }

      const buttonID = filteredButtons[0].id;
      const buttonDetail = await this.$_Flexi_retrieveButton({ ...commonParams, buttonID });

      if (
        buttonDetail.evidence === button.evidence
        && buttonDetail.kod === button.code
        && buttonDetail.url === `${button.targetUrl}?company=\${companyUrl}&evidence=\${evidence}&username=\${user.kod}&token=\${authSessionId}`
        && buttonDetail.title === button.title
        && buttonDetail.description === button.description
        && buttonDetail.location === 'list'
        && buttonDetail.browser === 'desktop'
      ) {
        return true;
      }

      await this.$_Flexi_deleteButton({ ...commonParams, button });

      return false;
    },
    async $_Flexi_retrieveButton({
      apiURL,
      username,
      password,
      token,
      company,
      buttonID,
    }) {
      const endpoint = `/c/${company}/custom-button/${buttonID}`;
      const response = await FlexiRequest({
        method: 'get',
        apiURL,
        endpoint,
        username,
        password,
        token,
      });
      return response.data.winstrom['custom-button'][0];
    },
    async $_Flexi_createButton({
      apiURL,
      username,
      password,
      token,
      company,
      button,
    }) {
      const endpoint = `/c/${company}/custom-button`;
      const data = {
        winstrom: {
          'custom-button': [
            {
              kod: button.code,
              url: `${button.targetUrl}?company=\${companyUrl}&evidence=\${evidence}&username=\${user.kod}&token=\${authSessionId}`,
              title: button.title,
              description: button.description,
              evidence: button.evidence,
              location: 'list',
              browser: 'desktop',
            },
          ],
        },
      };
      await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        data,
        username,
        password,
        token,
      });
      return true;
    },
    async $_Flexi_deleteButton({
      apiURL,
      username,
      password,
      token,
      company,
      button,
    }) {
      const endpoint = `/c/${company}/custom-button`;
      const data = {
        winstrom: {
          'custom-button': [
            {
              '@action': 'delete',
              id: `code:${button.code}`,
            },
          ],
        },
      };
      await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        data,
        username,
        password,
        token,
      });
      return true;
    },
    async $_Flexi_addEntry({
      apiURL,
      username,
      password,
      token,
      company,
      evidence,
      entry,
    }) {
      const endpoint = `/c/${company}/${evidence}`;
      const data = {
        winstrom: {
          [evidence]: [entry],
        },
      };
      const response = await FlexiRequest({
        method: 'post',
        apiURL,
        endpoint,
        data,
        username,
        password,
        token,
      });
      return response;
    },
    async $_Flexi_addAttachment({
      apiURL,
      username,
      password,
      token,
      ref,
      company,
      evidence,
      id,
      filename,
      note,
      link,
    }) {
      const endpoint = `${ref || `/c/${company}/${evidence}/${id}`}/prilohy/new/documentmanager`;
      const attachment = await FlexiRequest({
        method: 'put',
        apiURL,
        endpoint,
        username,
        password,
        token,
      });

      const attachmentRef = attachment.data.winstrom.results[0].ref;
      const data = {
        winstrom: {
          priloha: [
            {
              nazSoub: filename,
              poznam: note,
              typK: 'typPrilohy.odkaz',
              prilozit: false,
              link,
            },
          ],
        },
      };
      await FlexiRequest({
        method: 'put',
        apiURL,
        endpoint: attachmentRef,
        data,
        username,
        password,
        token,
      });
      return true;
    },
  },
};

export default Flexi;
